import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Inspector } from '@react-email/editor/ui';
import { Text } from '@mantine/core';
import styled from 'styled-components';

import { useInspectorPresets } from '../../hooks/useInspectorPresets';
import { BRAND_PRESET_COLORS, withCustomColors } from '../../utils/inspectorColors';
import { closestPresetValue } from '../../utils/inspectorPresets';
import {
	humanizeStyleLabel,
	isHiddenDocumentInput,
	PADDING_SIDES,
	sideFamilyFor,
	visibleDocumentGroups,
} from '../../utils/documentStyleGroups';
import { ColorSwatchRow } from './ColorSwatchRow';
import { DocumentStyleField } from './DocumentStyleField';
import { InspectorSection } from './InspectorSection';
import { PresetRow } from './PresetRow';

interface Props {
	customColors: string[];
	onCustomColorUsed: (hex: string) => void;
	backgroundColor: string;
	onBackgroundColorChange: (value: string) => void;
}

const FrameNote = styled.div`
	padding: 0.75rem 1rem;
	border-top: 1px solid var(--mantine-color-gray-4);
`;

export const GlobalProperties = ({
	customColors,
	onCustomColorUsed,
	backgroundColor,
	onBackgroundColorChange,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const presets = useInspectorPresets();

	const presetsByProp = {
		fontSize: presets.fontSize,
		lineHeight: presets.lineHeight,
		borderRadius: presets.cornerRadius,
		borderWidth: presets.borderWidth,
	};

	return (
		<Inspector.Document>
			{(ctx) => (
				<>
					<InspectorSection
						title={tMipManage('email_templates.editor.inspector.page_background', {
							defaultValue: 'Page background',
						})}
					>
						<ColorSwatchRow
							label={tMipManage('email_templates.editor.inspector.color', {
								defaultValue: 'Color',
							})}
							value={backgroundColor}
							presetColors={withCustomColors(BRAND_PRESET_COLORS, customColors)}
							onChange={onBackgroundColorChange}
							onCustomColorUsed={onCustomColorUsed}
						/>
					</InspectorSection>
					{visibleDocumentGroups(ctx.styles).map((group) => {
						const collapsed = new Set<string>();

						return (
							<InspectorSection
								key={group.title}
								title={humanizeStyleLabel(group.title)}
							>
								{group.inputs.map((input) => {
									if (
										isHiddenDocumentInput(
											input.prop,
											group.inputs.map((item) => item.prop)
										)
									) {
										return null;
									}

									const classReference =
										input.classReference ?? group.classReference;
									if (!classReference) {
										return null;
									}

									const sides = sideFamilyFor(input.prop);
									if (sides) {
										const isPadding = sides === PADDING_SIDES;
										const key = isPadding ? 'padding' : 'margin';
										if (collapsed.has(key)) {
											return null;
										}
										collapsed.add(key);
										return (
											<PresetRow
												key={key}
												label={
													isPadding
														? tMipManage(
																'email_templates.editor.inspector.padding',
																{ defaultValue: 'Padding' }
															)
														: tMipManage(
																'email_templates.editor.inspector.margin',
																{ defaultValue: 'Margin' }
															)
												}
												options={presets.spacing}
												value={closestPresetValue(
													presets.spacing,
													input.value
												)}
												onChange={(value) =>
													ctx.batchSetGlobalStyle(
														group.inputs
															.filter((item) =>
																sides.includes(item.prop)
															)
															.map((item) => ({
																classReference,
																property: item.prop,
																value,
															}))
													)
												}
											/>
										);
									}

									const presetOptions =
										presetsByProp[input.prop as keyof typeof presetsByProp];
									if (presetOptions) {
										return (
											<PresetRow
												key={input.prop}
												label={humanizeStyleLabel(input.label)}
												options={presetOptions}
												value={closestPresetValue(
													presetOptions,
													input.value
												)}
												onChange={(value) =>
													ctx.setGlobalStyle(
														classReference,
														input.prop,
														value
													)
												}
											/>
										);
									}

									if (input.type === 'color') {
										return (
											<ColorSwatchRow
												key={input.prop}
												label={humanizeStyleLabel(input.label)}
												value={input.value}
												presetColors={withCustomColors(
													BRAND_PRESET_COLORS,
													customColors
												)}
												onChange={(value) =>
													ctx.setGlobalStyle(
														classReference,
														input.prop,
														value
													)
												}
												onCustomColorUsed={onCustomColorUsed}
											/>
										);
									}

									return (
										<DocumentStyleField
											key={input.prop}
											input={input}
											onChange={(value) =>
												ctx.setGlobalStyle(
													classReference,
													input.prop,
													value
												)
											}
										/>
									);
								})}
							</InspectorSection>
						);
					})}
					<FrameNote>
						<Text fz="0.75rem" c="gray.9">
							{tMipManage('email_templates.editor.inspector.frame_note', {
								defaultValue:
									'The logo and footer come from the shared Dealstack email frame.',
							})}
						</Text>
					</FrameNote>
				</>
			)}
		</Inspector.Document>
	);
};
